import { useEffect, useState } from "react";

import { TextField, DatePicker, AutocompleteMultipleSelect, Button, InfoPanel } from "@king-ict/eupisi-ui/components";
import { DocumentData, CodeBook } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getUserFromOibRequest } from "../../api/nisooApi";
import { ButtonWithValidation } from "../../components/ButtonWithValidation";
import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { DocumentType } from "../../models/Document";
import { AndragogicLeader, AndragogicLeaderDeleteState, Institution, InstitutionLocation, LocationState } from "../../models/Institution";
import styles from "./Institution.module.scss";


export interface AndragogicLeaderPanelV2Props {
    name: string;
    andragogicLeaders: AndragogicLeader[];
    locations: InstitutionLocation[];
    institution: Institution;
    readOnly?: boolean;
    onChange: (e: any) => void;
    getErrorForPath: (path: string) => string | undefined;
    doValidations: () => boolean | undefined;
}

export default function AndragogicLeaderPanelV2({ name, andragogicLeaders, locations, institution, readOnly = false, onChange, getErrorForPath, doValidations }: AndragogicLeaderPanelV2Props): JSX.Element {
    const { t } = useTranslation();

    const [filteredLocations, setFilteredLocations] = useState<CodeBook[]>([] as CodeBook[]);
    const [userNotFound, setUserNotFound] = useState<boolean[]>([] as boolean[]);
    const [userFound, setUserFound] = useState<boolean[]>([] as boolean[]);

    useEffect(() => {
        fillLocations('');
    }, []);

    const fillLocations = (filter: string) => {
        const usedLocations = andragogicLeaders.map(x => x.locations).flat();
        var tempLocations: CodeBook[];
        var headquarterLocation: CodeBook = { id: -1, name: `${institution.address}, ${institution.town.name}, ${institution.town.municipalityName}` };

        if (filter != '') {
            tempLocations = locations
                .filter(x => x.address
                    && (x.activityState === LocationState.Active || (x.deactivateDate && new Date(x.deactivateDate) > new Date()))
                    && (x.address.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
                        x.town?.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
                        x.town?.municipalityName.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))
                )
                .map((a) => { return { id: a.locationDetailId, name: `${a.address}, ${a.town.name}, ${a.town.municipalityName}` } });

            if (institution.id !== 0 && (institution.address.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
                institution.town?.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
                institution.town?.municipalityName.toLocaleLowerCase().includes(filter.toLocaleLowerCase()))) {
                tempLocations.push(headquarterLocation);
            }

        }
        else {
            tempLocations = locations
                .filter(x =>
                    x.address &&
                    x.activityState === LocationState.Active || ((x.deactivateDate && new Date(x.deactivateDate) > new Date())))
                .map((a) => { return { id: a.locationDetailId, name: `${a.address}, ${a.town.name}, ${a.town.municipalityName}` } });

            if (institution.id !== 0) {
                tempLocations.push(headquarterLocation);
            }
        }

        tempLocations = tempLocations.filter(x => usedLocations.filter(y => y.id === x.id).length === 0);
        setFilteredLocations(tempLocations);
        // setFilteredLocations(tempLocations.filter(x => usedLocations.filter(y => y.id === x.id && y.name === x.name).length === 0));
    };

    const handleOibChange = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].oib = event.target.value;
        newAndragogicLeaderList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleFirstNameChange = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].firstName = event.target.value;
        newAndragogicLeaderList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleLastNameChange = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].lastName = event.target.value;
        newAndragogicLeaderList[index].oibChecked = false;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleCopyOfDiplomaAdded = (event: any, index: number, file: File) => {
        var copyOfDiplomaDocument: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };

        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].copyOdDiploma = copyOfDiplomaDocument;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });

    };

    const handleCopyOfDiplomaRemoved = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].copyOdDiploma = undefined;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleAdditionalDocumentAdded = (event: any, index: number, file: File) => {
        var additionalDocumentDocument: DocumentData = {
            id: -1,
            fileName: file.name,
            file: file
        };

        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].additionalDocument = additionalDocumentDocument;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleAdditionalDocumentRemoved = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].additionalDocument = undefined;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleDateFromChange = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].dateFrom = event.target.value;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleDateToChange = (event: any, index: number) => {        
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].dateTo = event.target.value;        
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const handleLocationsChange = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].locations = event.target.value;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });


        var tmpLocations: CodeBook[] = event.target.value;

        setFilteredLocations(filteredLocations.filter(x => tmpLocations.filter(y => y.id === x.id && y.name === x.name).length === 0));
    };

    const handleLocationFilterChange = async (filter: string) => {
        if (locations) {
            fillLocations(filter);
        }
    };

    const handleRemoveAndragogicLeader = (event: any, index: number) => {
        var newAndragogicLeaderList = andragogicLeaders.slice();
        newAndragogicLeaderList[index].deleteState = AndragogicLeaderDeleteState.Deleted;
        onChange({
            ...event,
            target: {
                ...event.target,
                name: name,
                value: newAndragogicLeaderList
            }
        });
    };

    const getAndragogicLeaderLocations = (index: number) => {
        if (andragogicLeaders[index].locations && andragogicLeaders[index].locations.length > 0) {
            return andragogicLeaders[index].locations.map(l => { return l.name }).join(", ");
        }
        return undefined;
    };

    const onCheckUserData = async (event: any, andragogicLeaderIndex: number) => {

        if (andragogicLeaders[andragogicLeaderIndex].oib.trim() != "" && andragogicLeaders[andragogicLeaderIndex].firstName.trim() != "" && andragogicLeaders[andragogicLeaderIndex].lastName.trim() != "") {
            var userInfo = await getUserFromOibRequest(andragogicLeaders[andragogicLeaderIndex].oib, andragogicLeaders[andragogicLeaderIndex].firstName, andragogicLeaders[andragogicLeaderIndex].lastName);

            if (userInfo && userInfo.firstName && userInfo.lastName) {
                var userFoundList = userFound.slice();
                userFoundList[andragogicLeaderIndex] = true;
                var userNotFoundList = userNotFound.slice();
                userNotFoundList[andragogicLeaderIndex] = false;

                setUserFound(userFoundList);
                setUserNotFound(userNotFoundList);

                var newAndragogicLeadersList = andragogicLeaders.slice();
                newAndragogicLeadersList[andragogicLeaderIndex].firstName = userInfo.firstName;
                newAndragogicLeadersList[andragogicLeaderIndex].lastName = userInfo.lastName;
                newAndragogicLeadersList[andragogicLeaderIndex].oibChecked = true;

                onChange({
                    ...event,
                    target: {
                        ...event.target,
                        name: name,
                        value: newAndragogicLeadersList
                    }
                });
                return;

            }
        }
        var userFoundList = userFound.slice();
        userFoundList[andragogicLeaderIndex] = false;
        var userNotFoundList = userNotFound.slice();
        userNotFoundList[andragogicLeaderIndex] = true;

        setUserFound(userFoundList);
        setUserNotFound(userNotFoundList);
    };

    return (
        <>
            {andragogicLeaders.filter(x => x.deleteState !== AndragogicLeaderDeleteState.Deleted).map((andragogicLeader, andragogicLeaderIndex) => {
                return (
                    <div className={styles.locationPanel}>
                        {!readOnly && userNotFound && userNotFound[andragogicLeaderIndex] &&
                            <Row>
                                <Col md={12}>
                                    <InfoPanel
                                        message={t("userRole.userNotFound", { oib: andragogicLeaders[andragogicLeaderIndex].oib.toString().padStart(11, "0") })}
                                        color="warning"
                                        fullWidth
                                    />
                                </Col>
                            </Row>
                        }
                        {!readOnly && userFound && userFound[andragogicLeaderIndex] &&
                            <Row>
                                <Col md={12}>
                                    <InfoPanel
                                        message={t("request.userFound")}
                                        color="success"
                                        fullWidth
                                    />
                                </Col>
                            </Row>
                        }
                        <Row>
                            <Col md={3}>
                                <TextField
                                    label={t("institution.andragogicLeader.oib")}
                                    value={andragogicLeader.oib}
                                    maxLength={11}
                                    name={nameof<typeof andragogicLeader>("oib")}
                                    onChange={(e) => handleOibChange(e, andragogicLeaderIndex)}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.oib`)}
                                    className={styles.textFieldLabel}
                                    readonly={readOnly}
                                    plaintext={readOnly}
                                />
                            </Col>
                            <Col md={3}>
                                <TextField
                                    label={t("institution.andragogicLeader.firstName")}
                                    value={andragogicLeader.firstName}
                                    name={nameof<typeof andragogicLeader>("firstName")}
                                    onChange={(e) => handleFirstNameChange(e, andragogicLeaderIndex)}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.firstName`)}
                                    className={styles.textFieldLabel}
                                    readonly={readOnly}
                                    plaintext={readOnly}
                                />
                            </Col>
                            <Col md={3}>
                                <TextField
                                    label={t("institution.andragogicLeader.lastName")}
                                    value={andragogicLeader.lastName}
                                    name={nameof<typeof andragogicLeader>("lastName")}
                                    onChange={(e) => handleLastNameChange(e, andragogicLeaderIndex)}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.lastName`)}
                                    className={styles.textFieldLabel}
                                    readonly={readOnly}
                                    plaintext={readOnly}
                                />
                            </Col>
                            {!readOnly &&
                                <Col md={3} className={styles.provjeraPodatakaContainer}>
                                    <ButtonWithValidation
                                        outline
                                        className={styles.checkDataButton}
                                        onClick={(e) => onCheckUserData(e, andragogicLeaderIndex)}
                                        errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.oibChecked`)}
                                    >
                                        {t("request.teacherCheck")}
                                    </ButtonWithValidation>
                                </Col>
                            }
                        </Row>
                        <Row>
                            <Col md={3}>
                                <UploadButton
                                    label={t("institution.andragogicLeader.copyOdDiploma")}
                                    name={nameof<typeof andragogicLeader>("copyOdDiploma")}
                                    onFileAdded={(file, e) => handleCopyOfDiplomaAdded(e, andragogicLeaderIndex, file)}
                                    onFileRemoved={(e) => handleCopyOfDiplomaRemoved(e, andragogicLeaderIndex)}
                                    value={andragogicLeader.copyOdDiploma}
                                    disabled={readOnly}
                                    toolTip={t("document.imagesAndPdfTooltip")}
                                    fileFilter={t("document.imagesAndPdfFilter")}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.copyOdDiploma`)}
                                    documentType={DocumentType.CopyOfDiploma}
                                />
                            </Col>
                            <Col md={3}>
                                {(!readOnly || andragogicLeader.additionalDocument) &&
                                    <UploadButton
                                        label={t("institution.andragogicLeader.additionalDocument")}
                                        name={nameof<typeof andragogicLeader>("additionalDocument")}
                                        onFileAdded={(file, e) => handleAdditionalDocumentAdded(e, andragogicLeaderIndex, file)}
                                        onFileRemoved={(e) => handleAdditionalDocumentRemoved(e, andragogicLeaderIndex)}
                                        value={andragogicLeader.additionalDocument}
                                        disabled={readOnly}
                                        toolTip={t("document.imagesAndPdfTooltip")}
                                        fileFilter={t("document.imagesAndPdfFilter")}
                                        errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.additionalDocument`)}
                                        documentType={DocumentType.AdditionalDocument}
                                    />
                                    ||
                                    <StaticField
                                        label={t("institution.andragogicLeader.additionalDocument")}
                                        value={andragogicLeader.additionalDocument?.fileName}
                                    />
                                }
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                {!readOnly &&
                                    <AutocompleteMultipleSelect<CodeBook>
                                        label={t("institution.andragogicLeader.locations")}
                                        name={nameof<typeof andragogicLeader>("locations")}
                                        value={andragogicLeader.locations || []}
                                        valueProp="id"
                                        textProp="name"
                                        onChange={(e) => handleLocationsChange(e, andragogicLeaderIndex)}
                                        selectOptions={filteredLocations || []}
                                        onFilterChange={handleLocationFilterChange}
                                        maximumSelectedItems={5}
                                        minimumInputLength={0}
                                        errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.locations`)}
                                        className={styles.textFieldLabel}
                                    />
                                }
                                {readOnly &&
                                    <Col md={12} className={styles.textFieldLabel}>
                                        <TextField
                                            label={t("institution.andragogicLeader.locations")}
                                            readonly={true}
                                            plaintext={true}
                                        />
                                        {andragogicLeader.locations.map((location: CodeBook) => {
                                            return (
                                                <div>
                                                    <TextField
                                                        value={location.name}
                                                        name={nameof<typeof location>("name")}
                                                        readonly={true}
                                                        plaintext={true}
                                                    />
                                                </div>
                                            )
                                        })}
                                    </Col>
                                }
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3} className={styles.textFieldLabel}>
                                <DatePicker
                                    label={t("common.dateFrom")}
                                    value={andragogicLeader.dateFrom}
                                    name={nameof<typeof andragogicLeader>("dateFrom")}
                                    onChange={(e) => handleDateFromChange(e, andragogicLeaderIndex)}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.dateFrom`)}
                                    isClearable={true}
                                    readonly={readOnly}
                                    plaintext={readOnly}
                                    minYear={new Date().getFullYear() - 25}
                                    maxYear={new Date().getFullYear() + 25}
                                />
                            </Col>
                            <Col md={3} className={styles.textFieldLabel}>
                                <DatePicker
                                    label={t("common.dateTo")}
                                    value={andragogicLeader.dateTo}
                                    name={nameof<typeof andragogicLeader>("dateTo")}
                                    onChange={(e) => handleDateToChange(e, andragogicLeaderIndex)}
                                    errorText={getErrorForPath(`${name}${andragogicLeaderIndex}.dateTo`)}
                                    isClearable={true}
                                    readonly={readOnly}
                                    plaintext={readOnly}
                                    minYear={new Date().getFullYear() - 25}
                                    maxYear={new Date().getFullYear() + 25}
                                />
                            </Col>
                            {!readOnly &&
                                <Col md={6} className={styles.removeAndragogicLeaderCol}>
                                    <Button
                                        boxless
                                        onClick={(e) => handleRemoveAndragogicLeader(e, andragogicLeaderIndex)}
                                    >
                                        {t("institution.andragogicLeader.removeAndragogicLeader")}
                                    </Button>
                                </Col>
                            }
                        </Row>
                    </div>
                )
            })}
        </>
    )
}